import _ from 'lodash';
import Tooltip from 'view/components/Tooltip';
import {
	SUBSCRIPTION_PLANS,
	SUBSCRIPTION_STATUSES,
} from 'constants/subscriptions';

// Stripe product IDs from user
export const STRIPE_PRODUCTS = {
	STARTER: 'prod_SrP6ig7uW2eSVp',
	PRO: 'prod_SpvPQxDeiec28w',
	BUSINESS: 'prod_SrP81ccFy6ov7d',
};

// Map local plan names to Stripe products
export const PLAN_TO_STRIPE_PRODUCT = {
	Starter: STRIPE_PRODUCTS.STARTER,
	Pro: STRIPE_PRODUCTS.PRO,
	Business: STRIPE_PRODUCTS.BUSINESS,
};

/**
 * Plan hierarchy for comparison purposes
 * Higher numbers indicate higher tier plans
 */
export const PLAN_HIERARCHY = {
	free: 0,
	starter: 1,
	pro: 2,
	business: 3,
	enterprise: 4,
};

export function getFallbackPricingData() {
	return [
		{
			id: SUBSCRIPTION_PLANS.FREE,
			name: 'Free',
			stripeProductId: null,
			monthlyPrice: 0,
			yearlyPrice: 0,
			monthlyPriceId: null,
			yearlyPriceId: null,
			subtitle: 'For Individual Owners & Small Landlords',
			buttonText: 'Select Plan',
			popular: false,
			features: [
				{ text: 'Up to 5 Properties', included: true },
				{ text: '2,000 Email Alerts/month', included: true },
				{ text: 'Property Reports for Your Portfolio', included: true },
				{
					text: (
						<span>
							Get Alerts on Your Portfolio{' '}
							<Tooltip
								text="Get new property alerts on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{ text: 'Property Updates on Property Changes', included: false },
				{ text: 'Portfolio support', included: false },
				{ text: '1 Seat', included: true },
			],
		},
		{
			id: SUBSCRIPTION_PLANS.STARTER,
			name: 'Starter',
			stripeProductId: STRIPE_PRODUCTS.STARTER,
			monthlyPrice: 75,
			yearlyPrice: 810,
			monthlyPriceId: 'price_1RvgI9GRB8wHY2n3AcWLCpPQ',
			yearlyPriceId: 'price_1RyD6VGRB8wHY2n3kDSrpF18',
			subtitle: 'For Small Property Managers',
			buttonText: 'Select Plan',
			popular: false,
			features: [
				{ text: 'Up to 15 Properties', included: true },
				{ text: '70,000 Email Alerts and Notifications/month', included: true },
				{
					text: (
						<span>
							Get Alerts on Your Portfolio{' '}
							<Tooltip
								text="Get new property alerts on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: (
						<span>
							Stay Notified on property changes{' '}
							<Tooltip
								text="Stay notified on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{ text: '1 Property Report for Your Portfolio', included: true },
				{ text: 'Email Support', included: true },
				{ text: '5 Seats', included: true },
			],
		},
		{
			id: SUBSCRIPTION_PLANS.PRO,
			name: 'Pro',
			stripeProductId: STRIPE_PRODUCTS.PRO,
			monthlyPrice: 120,
			yearlyPrice: 1296,
			monthlyPriceId: 'price_1RvgGMGRB8wHY2n3ehvts24S',
			yearlyPriceId: 'price_1RyD8BGRB8wHY2n3s3sbmbIt',
			subtitle: 'For Mid-Size Managers & Developers',
			buttonText: 'Select Plan',
			popular: true,
			features: [
				{ text: 'Up to 30 Properties', included: true },
				{ text: 'Unlimited Email Alerts and Notifications', included: true },
				{
					text: (
						<span>
							Get Alerts on Your Portfolio{' '}
							<Tooltip
								text="Get new property alerts on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>{' '}
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: (
						<span>
							Stay Notified on property changes{' '}
							<Tooltip
								text="Stay notified on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{ text: '2 Property Reports for Your Portfolio', included: true },
				{ text: 'Call/Email Support', included: true },
				{ text: '10 Seats', included: true },
			],
		},
		{
			id: SUBSCRIPTION_PLANS.BUSINESS,
			name: 'Business',
			stripeProductId: STRIPE_PRODUCTS.BUSINESS,
			monthlyPrice: 199,
			yearlyPrice: 2148,
			monthlyPriceId: 'price_1RvgJrGRB8wHY2n3IIaJ4Cgd',
			yearlyPriceId: 'price_1RyD4zGRB8wHY2n3lzUdxUKc',
			subtitle: 'For Large Portfolios & Regional Firms',
			buttonText: 'Select Plan',
			popular: false,
			features: [
				{ text: 'Up to 50 Properties', included: true },
				{ text: 'Unlimited Email Alerts and Notifications', included: true },
				{
					text: (
						<span>
							Get Alerts on Your Portfolio{' '}
							<Tooltip
								text="Get new property alerts on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: (
						<span>
							Stay Notified on property changes{' '}
							<Tooltip
								text="Stay notified on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: 'Unlimited Property Reports for Your Portfolio',
					included: true,
				},
				{ text: 'Call/Email Support', included: true },
				{ text: 'Unlimited Seats', included: true },
			],
		},
		{
			id: SUBSCRIPTION_PLANS.ENTERPRISE,
			name: 'Enterprise',
			stripeProductId: null,
			monthlyPrice: null,
			yearlyPrice: null,
			monthlyPriceId: null,
			yearlyPriceId: null,
			subtitle: 'For Institutions & Nationwide Operators',
			buttonText: 'Get a Quote',
			popular: false,
			isCustom: true,
			features: [
				{ text: 'Custom Number of Properties', included: true },
				{ text: 'Unlimited Email Alerts and Notifications', included: true },
				{
					text: (
						<span>
							Get Alerts on Your Portfolio{' '}
							<Tooltip
								text="Get new property alerts on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: (
						<span>
							Stay Notified on property changes{' '}
							<Tooltip
								text="Stay notified on your property from DOB, HPD, DOF, HPD, DOH, FDNY, ECB, DOT, DEC, DEP, DSNY and more!"
								direction="bottom"
							>
								<span
									className="bi bi-question-circle"
									style={{ cursor: 'pointer' }}
								></span>
							</Tooltip>
						</span>
					),
					included: true,
				},
				{
					text: 'Unlimited Property Reports for Your Portfolio',
					included: true,
				},
				{ text: 'Dedicated Portfolio Manager', included: true },
				{ text: 'Unlimited Seats', included: true },
				{ text: 'Priority Support', included: true },
			],
		},
	];
}

/**
 * Combines available subscription plans from API with fallback data
 * Groups monthly/yearly variants and creates unified plan objects
 *
 * @param {Array} availablePlans - Array of subscription plans from API
 * @param {Array} fallbackPlans - Optional fallback plans array (if not provided, uses getFallbackPricingData())
 * @returns {Array} Array of combined plan objects suitable for pricing display
 */
export function combineSubscriptionPlans(
	availablePlans = [],
	fallbackPlans = null,
) {
	if (!availablePlans || availablePlans.length === 0) {
		return [];
	}

	// Get fallback pricing data for comprehensive features
	const fallbackData = fallbackPlans || getFallbackPricingData();
	const fallbackPlanMap = {};
	fallbackData.forEach((plan) => {
		fallbackPlanMap[plan.id] = plan;
	});

	// Group plans by stripe_product_id (plans with same product_id are monthly/yearly variants)
	const groupedPlans = _.groupBy(availablePlans, 'stripe_product_id');

	// Transform grouped plans into pricing table format
	const transformedPlans = Object.entries(groupedPlans)
		.map(([stripeProductId, planVariants]) => {
			// Find monthly and yearly variants
			const monthlyPlan = planVariants.find((p) => p.interval === 'monthly');
			const yearlyPlan = planVariants.find((p) => p.interval === 'yearly');

			// Use the first plan as base (they should have same product info)
			const basePlan = planVariants[0];

			// Extract plan type from name (Starter, Pro, Business)
			const planType = basePlan.name.replace('Violerts ', '').toLowerCase();

			// Get features from fallback data for comprehensive feature descriptions
			const fallbackPlan = fallbackPlanMap[planType];
			const features = fallbackPlan
				? fallbackPlan.features
				: [
						// Fallback features if no match found
						...(monthlyPlan?.features || yearlyPlan?.features || []).map(
							(feature) => ({ text: feature, included: true }),
						),
					];

			// Create plan object for pricing table
			const plan = {
				id: planType,
				name: basePlan.name.replace('Violerts ', ''), // Remove 'Violerts' prefix
				subtitle: fallbackPlan
					? fallbackPlan.subtitle
					: basePlan.description.split('.')[0] + '.', // Use fallback subtitle if available
				monthlyPrice: monthlyPlan ? monthlyPlan.price_dollars : 0,
				yearlyPrice: yearlyPlan ? yearlyPlan.price_dollars : 0,
				monthlyPriceId: monthlyPlan?.stripe_price_id,
				yearlyPriceId: yearlyPlan?.stripe_price_id,
				monthlyPlanId: monthlyPlan?.id, // Database plan ID for monthly
				yearlyPlanId: yearlyPlan?.id, // Database plan ID for yearly
				buttonText: fallbackPlan ? fallbackPlan.buttonText : 'Get Started',
				popular:
					monthlyPlan?.is_popular ||
					yearlyPlan?.is_popular ||
					(fallbackPlan ? fallbackPlan.popular : false),
				isCustom: false,
				features: features, // Use comprehensive features from fallback data
			};

			return plan;
		})
		.sort((a, b) => {
			// Sort by price: Starter, Pro, Business
			const order = { starter: 1, pro: 2, business: 3 };
			return (order[a.id] || 999) - (order[b.id] || 999);
		});

	// Get Free and Enterprise plans from fallback data for consistency
	const freePlan = fallbackPlanMap['free'] || {
		id: 'free',
		name: 'Free',
		subtitle: 'Perfect for getting started',
		monthlyPrice: 0,
		yearlyPrice: 0,
		buttonText: 'Start Free',
		popular: false,
		isCustom: false,
		features: [
			{ text: 'Basic features', included: true },
			{ text: 'Limited support', included: true },
			{ text: 'Advanced features', included: false },
		],
	};

	const enterprisePlan = fallbackPlanMap[SUBSCRIPTION_PLANS.ENTERPRISE] || {
		id: SUBSCRIPTION_PLANS.ENTERPRISE,
		name: 'Enterprise',
		subtitle: 'For large organizations',
		monthlyPrice: 0,
		yearlyPrice: 0,
		buttonText: 'Contact Sales',
		popular: false,
		isCustom: true,
		features: [
			{ text: 'All features', included: true },
			{ text: 'Priority support', included: true },
			{ text: 'Custom integrations', included: true },
		],
	};

	if (transformedPlans) {
		return _.uniqBy([freePlan, ...transformedPlans, enterprisePlan], 'id');
	}

	return _.uniqBy([freePlan, enterprisePlan], 'id');
}

/**
 * Gets the current subscription plan details from available plans
 *
 * @param {Object} currentSubscription - Current subscription object
 * @param {Array} availablePlans - Array of available subscription plans
 * @param {boolean} hasSubscription - Whether user has an active subscription
 * @returns {Object|null} Current subscription plan object or null
 */
export function getCurrentSubscriptionPlan(
	currentSubscription,
	availablePlans,
	hasSubscription = true,
) {
	if (!currentSubscription?.plan?.id) return null;

	// Find the current plan in availablePlans based on subscription data
	const currentPlanId = currentSubscription.plan?.id;
	const currentPlan = availablePlans.find((plan) => plan.id === currentPlanId);

	return currentPlan;
}

/**
 * Determines button text based on subscription status and plan comparison
 *
 * @param {Object} plan - Plan object to get button text for
 * @param {Object} options - Options object containing subscription state
 * @param {boolean} options.isLoggedIn - Whether user is logged in
 * @param {boolean} options.hasSubscription - Whether user has a subscription
 * @param {Object} options.currentSubscription - Current subscription object
 * @param {Array} options.availablePlans - Available plans array
 * @param {boolean} options.isProcessing - Whether checkout is in progress
 * @returns {string} Button text for the plan
 */
export function getPlanButtonText(plan, options = {}) {
	const {
		isLoggedIn = false,
		hasSubscription = false,
		currentSubscription = null,
		availablePlans = [],
		isProcessing = false,
	} = options;

	// For processing state
	if (isProcessing) {
		return 'Processing...';
	}

	// For free plan
	if (plan.id === 'free') {
		return plan.buttonText || 'Start Free';
	}

	// For custom/enterprise plans
	if (plan.isCustom) {
		return plan.buttonText || 'Contact Sales';
	}

	// If user is not logged in
	if (!isLoggedIn) {
		return plan.buttonText || 'Get Started';
	}

	// If user doesn't have a subscription
	if (!hasSubscription) {
		return plan.buttonText || 'Get Started';
	}

	// If user has a subscription, compare plans
	const currentPlan = getCurrentSubscriptionPlan(
		currentSubscription,
		availablePlans,
		hasSubscription,
	);
	if (!currentPlan) {
		return plan.buttonText || 'Get Started';
	}

	// Get the current plan type for comparison
	const currentPlanType = currentPlan.name
		.replace('Violerts ', '')
		.toLowerCase();
	const targetPlanType = plan.id;

	const currentPlanLevel = PLAN_HIERARCHY[currentPlanType] || 0;
	const targetPlanLevel = PLAN_HIERARCHY[targetPlanType] || 0;

	// Same plan
	if (currentPlanType === targetPlanType) {
		return 'Current Plan';
	}

	// Upgrade or downgrade
	if (targetPlanLevel > currentPlanLevel) {
		return 'Upgrade Plan';
	} else if (targetPlanLevel < currentPlanLevel) {
		return 'Downgrade';
	}

	// Fallback
	return plan.buttonText || 'Get Started';
}

/**
 * Determines if a plan button should be disabled
 *
 * @param {Object} plan - Plan object to check
 * @param {Object} options - Options object containing subscription state
 * @param {boolean} options.hasSubscription - Whether user has a subscription
 * @param {Object} options.currentSubscription - Current subscription object
 * @param {Array} options.availablePlans - Available plans array
 * @param {boolean} options.isProcessing - Whether checkout is in progress
 * @returns {boolean} Whether the plan button should be disabled
 */
export function isPlanButtonDisabled(plan, options = {}) {
	const {
		hasSubscription = false,
		currentSubscription = null,
		availablePlans = [],
		isProcessing = false,
	} = options;

	// Always disable during processing
	if (isProcessing) {
		return true;
	}

	// If user has subscription and this is their current plan, disable it
	if (hasSubscription && currentSubscription) {
		const currentPlan = getCurrentSubscriptionPlan(
			currentSubscription,
			availablePlans,
			hasSubscription,
		);
		if (currentPlan) {
			const currentPlanType = currentPlan.name
				.replace('Violerts ', '')
				.toLowerCase();
			if (currentPlanType === plan.id) {
				return true;
			}
		}
	}

	return false;
}

/**
 * Compares two plans and determines the relationship (same, upgrade, downgrade)
 *
 * @param {string} currentPlanId - Current plan ID
 * @param {string} targetPlanId - Target plan ID
 * @returns {Object} Comparison result with type and level difference
 */
export function comparePlans(currentPlanId, targetPlanId) {
	const currentLevel = PLAN_HIERARCHY[currentPlanId?.toLowerCase()] || 0;
	const targetLevel = PLAN_HIERARCHY[targetPlanId?.toLowerCase()] || 0;

	const levelDifference = targetLevel - currentLevel;

	let type = 'same';
	if (levelDifference > 0) {
		type = 'upgrade';
	} else if (levelDifference < 0) {
		type = 'downgrade';
	}

	return {
		type,
		levelDifference,
		currentLevel,
		targetLevel,
	};
}

/**
 * Extracts plan type from plan name (removes 'Violerts' prefix)
 *
 * @param {string} planName - Full plan name
 * @returns {string} Plan type in lowercase
 */
export function extractPlanType(planName) {
	return planName.replace('Violerts ', '').toLowerCase();
}

/**
 * Gets the appropriate database plan ID based on billing interval
 *
 * @param {Object} plan - Plan object
 * @param {boolean} isYearly - Whether yearly billing is selected
 * @returns {string|null} Database plan ID
 */
export function getPlanDatabaseId(plan, isYearly = false) {
	return isYearly ? plan.yearlyPlanId : plan.monthlyPlanId;
}

/**
 * Gets the appropriate Stripe price ID based on billing interval
 *
 * @param {Object} plan - Plan object
 * @param {boolean} isYearly - Whether yearly billing is selected
 * @returns {string|null} Stripe price ID
 */
export function getPlanPriceId(plan, isYearly = false) {
	return isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;
}
/**
 * Gets the appropriate plan price based on billing interval
 *
 * @param {Object} plan - Plan object
 * @param {boolean} isYearly - Whether yearly billing is selected
 * @returns {number} Plan price
 */
export function getPlanPrice(plan, isYearly = false) {
	if (isYearly) {
		// For yearly plans, show monthly equivalent price
		return plan.yearlyPrice ? Math.floor(plan.yearlyPrice / 12) : 0;
	}
	return plan.monthlyPrice || 0;
}

/**
 * Stores plan selection data in localStorage for post-authentication checkout
 *
 * @param {Object} plan - Plan object to store
 * @param {boolean} isYearly - Whether yearly billing is selected
 * @param {boolean} withTrial - Whether this is a trial signup
 * @param {boolean} withSmsAddon - Whether SMS Add-on was selected
 */
export function storePendingCheckout(
	plan,
	isYearly = false,
	withTrial = false,
	withSmsAddon = false,
) {
	localStorage.setItem(
		'selectedPlan',
		JSON.stringify({
			...plan,
			features: [],
			planId: plan.id,
			planName: plan.name,
			billingInterval: isYearly ? 'yearly' : 'monthly',
			priceId: getPlanDatabaseId(plan, isYearly),
			withTrial,
			withSmsAddon,
			timestamp: Date.now(), // For expiration if needed
		}),
	);
}

/**
 * Retrieves pending checkout data from localStorage
 *
 * @returns {Object|null} Parsed plan data or null if not found/invalid
 */
export function getPendingCheckout() {
	try {
		const selectedPlan = localStorage.getItem('selectedPlan');
		if (!selectedPlan) return null;

		const planData = JSON.parse(selectedPlan);
		// Optional: Check if timestamp is too old (24 hours)
		if (
			planData.timestamp &&
			Date.now() - planData.timestamp > 24 * 60 * 60 * 1000
		) {
			localStorage.removeItem('selectedPlan');
			return null;
		}

		return planData;
	} catch (error) {
		console.error('Error parsing pending checkout data:', error);
		localStorage.removeItem('selectedPlan');
		return null;
	}
}

/**
 * Clears pending checkout data from localStorage
 */
export function clearPendingCheckout() {
	localStorage.removeItem('selectedPlan');
}

/**
 * Query parameter carrying an organization invitation token through the
 * sign-in and sign-up round trip. AcceptInvitation writes it, SignUp reads it
 * and getPostAuthRedirect below returns the authenticated visitor to the
 * invitation, so the three must agree on one spelling.
 */
export const INVITATION_TOKEN_PARAM = 'invitation_token';

/**
 * Determines the correct redirect URL after authentication based on URL params and localStorage
 *
 * An invitation token in the URL is the visitor's in-flight intent, arriving
 * from the invitation link itself, so it outranks a pending checkout, which is
 * a stored selection up to 24 hours old.
 *
 * Only sign-in returns to the invitation. A sign-up that carries the token
 * has already joined the organization: POST /api/signup accepts the
 * invitation inside the same transaction, so the token no longer resolves and
 * returning to it would show the new member a dead-invitation error. SignUp
 * therefore passes `invitationConsumed: true`.
 *
 * @param {URLSearchParams} urlParams - URL search parameters
 * @param {{invitationConsumed?: boolean}} [options] - `invitationConsumed`
 *   is true when the authentication itself accepted the invitation.
 * @returns {string} Redirect URL path
 */
export function getPostAuthRedirect(
	urlParams,
	{ invitationConsumed = false } = {},
) {
	const invitationToken = invitationConsumed
		? null
		: urlParams?.get(INVITATION_TOKEN_PARAM);

	if (invitationToken) {
		// Back to the invitation, which now has a session to accept with.
		return `/accept-invitation/${encodeURIComponent(invitationToken)}`;
	}

	const selectedPlan = getPendingCheckout();

	if (selectedPlan) {
		return '/'; // Redirect to pricing page to trigger checkout
	}

	return '/dashboard/overview'; // Default redirect
}

// ============================================================================
// Subscription status helpers
// ============================================================================

const normalizeStatus = (status) => (status ? status.toLowerCase() : '');

export function getSubscriptionStatusFromObject(subscription) {
	if (!subscription) return '';
	// Support both `status` and `stripe_status` fields
	return (
		normalizeStatus(subscription.status) ||
		normalizeStatus(subscription.stripe_status)
	);
}

export function isSubscriptionActiveStatus(status) {
	return normalizeStatus(status) === SUBSCRIPTION_STATUSES.ACTIVE;
}

export function isSubscriptionCancelledStatus(status) {
	const normalized = normalizeStatus(status);
	return (
		normalized === SUBSCRIPTION_STATUSES.CANCELED ||
		normalized === SUBSCRIPTION_STATUSES.CANCELLED
	);
}

export function isSubscriptionActive(subscription) {
	return isSubscriptionActiveStatus(
		getSubscriptionStatusFromObject(subscription),
	);
}

export function isSubscriptionCancelled(subscription) {
	return isSubscriptionCancelledStatus(
		getSubscriptionStatusFromObject(subscription),
	);
}

export function getSubscriptionStatusBadgeClass(statusOrSubscription) {
	const status =
		typeof statusOrSubscription === 'string'
			? statusOrSubscription
			: getSubscriptionStatusFromObject(statusOrSubscription);

	const normalized = normalizeStatus(status);

	if (normalized === SUBSCRIPTION_STATUSES.ACTIVE) return 'badge-success';
	if (isSubscriptionCancelledStatus(normalized)) return 'badge-danger';
	if (
		normalized === SUBSCRIPTION_STATUSES.PAUSED ||
		normalized === SUBSCRIPTION_STATUSES.SUSPENDED
	) {
		return 'badge-warning';
	}
	if (normalized === SUBSCRIPTION_STATUSES.PENDING) return 'badge-info';
	if (normalized === SUBSCRIPTION_STATUSES.EXPIRED) return 'badge-secondary';

	return 'badge-secondary';
}

export function getSubscriptionStatusDisplay(statusOrSubscription) {
	const status =
		typeof statusOrSubscription === 'string'
			? statusOrSubscription
			: getSubscriptionStatusFromObject(statusOrSubscription);
	const normalized = normalizeStatus(status);

	switch (normalized) {
		case SUBSCRIPTION_STATUSES.ACTIVE:
			return 'Active';
		case SUBSCRIPTION_STATUSES.CANCELED:
		case SUBSCRIPTION_STATUSES.CANCELLED:
			return 'Cancelled';
		case SUBSCRIPTION_STATUSES.PAUSED:
		case SUBSCRIPTION_STATUSES.SUSPENDED:
			return 'Paused';
		case SUBSCRIPTION_STATUSES.PENDING:
			return 'Pending';
		case SUBSCRIPTION_STATUSES.EXPIRED:
			return 'Expired';
		default:
			return status && status.length
				? status.charAt(0).toUpperCase() + status.slice(1)
				: '';
	}
}

export function isEnterprisePlan(plan) {
	if (!plan?.name) return false;
	return (
		plan?.name?.toLowerCase().includes(SUBSCRIPTION_PLANS.ENTERPRISE) ||
		plan?.slug?.toLowerCase().includes(SUBSCRIPTION_PLANS.ENTERPRISE)
	);
}
